import ScopeDetailsRichText from "../BusinessOrdersManagement/ScopeDetailsRichText";

function isLicenseExpired(value) {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(23, 59, 59, 999);
  return d.getTime() < Date.now();
}

function findVendorById(vendors, vendorId) {
  const id = String(vendorId ?? "").trim();
  if (!id) return null;
  return (Array.isArray(vendors) ? vendors : []).find((v) => String(v._id) === id) || null;
}

function isScopeVendorExpired(vendors, vendorId) {
  const vendor = findVendorById(vendors, vendorId);
  if (!vendor) return false;
  return isLicenseExpired(vendor.licenseExpiryDate);
}

/**
 * Shared editable "Scope of Work" deliverable list — used by any screen that
 * edits a Business Order Invoice's `scopeOfWork` array, so the deliverable
 * fields (title, department, vendor, tax/total, details) stay identical
 * everywhere it's edited.
 */
const ScopeOfWorkEditor = ({
  scopeOfWork,
  departments = [],
  departmentsLoading = false,
  departmentsLoadError = "",
  vendors = [],
  vendorsLoading = false,
  vendorsLoadError = "",
  departmentErrorIndices = [],
  expiredVendorErrorIndices = [],
  dueDateErrorIndices = [],
  onTitleChange,
  onFieldChange,
  onDetailsChange,
  onAdd,
  onRemove,
  keySalt = "",
}) => {
  if (!Array.isArray(scopeOfWork) || !scopeOfWork.length) {
    return (
      <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={onAdd}>
        Add deliverable
      </button>
    );
  }

  return (
    <>
      {scopeOfWork.map((item, index) => (
        <div key={`scope-${index}-${keySalt}`} className="bom-scope-item bom-scope-item-edit">
          <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
            <span className="small text-muted">Deliverable {index + 1}</span>
            <button
              type="button"
              className="btn btn-link btn-sm text-danger p-0"
              onClick={() => onRemove(index)}
            >
              Remove
            </button>
          </div>
          <input
            className="bom-input form-control form-control-sm mb-2"
            placeholder="Title"
            value={item.title}
            onChange={(e) => onTitleChange(index, e.target.value)}
          />
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-department-${index}`}>
                Department *
              </label>
              <select
                id={`sow-department-${index}`}
                className="bom-input form-select form-select-sm"
                value={item.departmentId ?? ""}
                onChange={(e) => onFieldChange(index, "departmentId", e.target.value)}
                disabled={departmentsLoading}
                required
              >
                <option value="">
                  {departmentsLoading
                    ? "Loading departments..."
                    : departmentsLoadError
                      ? "Unable to load departments"
                      : "Select department"}
                </option>
                {departments.map((dept) => (
                  <option key={String(dept._id)} value={String(dept._id)}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
              {departmentsLoadError ? (
                <p className="small text-warning mb-0 mt-1">{departmentsLoadError}</p>
              ) : null}
              {departmentErrorIndices.includes(index + 1) ? (
                <p className="small text-danger mb-0 mt-1">
                  Department is required for Deliverable {index + 1}.
                </p>
              ) : null}
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-vendor-${index}`}>
                Vendor
              </label>
              <select
                id={`sow-vendor-${index}`}
                className="bom-input form-select form-select-sm"
                value={item.vendorId ?? ""}
                onChange={(e) => onFieldChange(index, "vendorId", e.target.value)}
                disabled={vendorsLoading}
              >
                <option value="">
                  {vendorsLoading
                    ? "Loading vendors..."
                    : vendorsLoadError
                      ? "Unable to load vendors"
                      : "Select vendor"}
                </option>
                {(() => {
                  const selectedId = String(item.vendorId ?? "").trim();
                  const selectedVendor = findVendorById(vendors, selectedId);
                  const showSavedExpired =
                    selectedId && selectedVendor && isLicenseExpired(selectedVendor.licenseExpiryDate);
                  return (
                    <>
                      {showSavedExpired ? (
                        <option value={selectedId}>
                          {selectedVendor.vendorName} (license expired)
                        </option>
                      ) : null}
                      {vendors.map((vendor) => (
                        <option
                          key={String(vendor._id)}
                          value={String(vendor._id)}
                          disabled={isLicenseExpired(vendor.licenseExpiryDate)}
                        >
                          {vendor.vendorName}
                          {isLicenseExpired(vendor.licenseExpiryDate) ? " (license expired)" : ""}
                        </option>
                      ))}
                    </>
                  );
                })()}
              </select>
              {vendorsLoadError ? (
                <p className="small text-warning mb-0 mt-1">{vendorsLoadError}</p>
              ) : null}
              {isScopeVendorExpired(vendors, item.vendorId) ||
              expiredVendorErrorIndices.includes(index + 1) ? (
                <p className="small text-danger mb-0 mt-1">
                  This vendor&apos;s license is expired. Please select another vendor.
                </p>
              ) : null}
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-due-date-${index}`}>
                Payment due date *
              </label>
              <input
                id={`sow-due-date-${index}`}
                type="date"
                className="bom-input form-control form-control-sm"
                value={item.dueDate ?? ""}
                onChange={(e) => onFieldChange(index, "dueDate", e.target.value)}
                required
              />
              {dueDateErrorIndices.includes(index + 1) ? (
                <p className="small text-danger mb-0 mt-1">
                  Payment due date is required for Deliverable {index + 1}.
                </p>
              ) : null}
            </div>
            <div className="col-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-tax-${index}`}>
                Tax amount
              </label>
              <input
                id={`sow-tax-${index}`}
                className="bom-input form-control form-control-sm"
                inputMode="decimal"
                placeholder="—"
                value={item.taxAmount ?? ""}
                onChange={(e) => onFieldChange(index, "taxAmount", e.target.value)}
              />
            </div>
            <div className="col-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-line-total-${index}`}>
                Total amount
              </label>
              <input
                id={`sow-line-total-${index}`}
                className="bom-input form-control form-control-sm"
                inputMode="decimal"
                placeholder="—"
                value={item.totalAmount ?? ""}
                onChange={(e) => onFieldChange(index, "totalAmount", e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-payment-currency-${index}`}>
                Payment currency
              </label>
              <select
                id={`sow-payment-currency-${index}`}
                className="bom-input form-select form-select-sm"
                value={item.paymentCurrency ?? ""}
                onChange={(e) => onFieldChange(index, "paymentCurrency", e.target.value)}
              >
                <option value="">Not set</option>
                <option value="SAR">SAR</option>
                <option value="AED">AED</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small text-muted mb-0" htmlFor={`sow-campaign-location-${index}`}>
                Country
              </label>
              <select
                id={`sow-campaign-location-${index}`}
                className="bom-input form-select form-select-sm"
                value={item.campaignLocation ?? ""}
                onChange={(e) => onFieldChange(index, "campaignLocation", e.target.value)}
              >
                <option value="">Not set</option>
                <option value="UAE">UAE</option>
                <option value="KSA">KSA</option>
              </select>
            </div>
          </div>
          <ScopeDetailsRichText
            key={`scope-details-${index}-${keySalt}`}
            details={item.details || []}
            editorId={`sow-${index}`}
            onDetailsChange={(lines) => onDetailsChange(index, lines)}
          />
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={onAdd}>
        Add deliverable
      </button>
    </>
  );
};

export default ScopeOfWorkEditor;
